import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapPin, Phone, Mail, Clock, Building } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

const ContactSection = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.contact-item',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [language]);

  const contactItems = [
    { icon: Building, label: t.contact.office, value: contactInfo.address },
    { icon: Phone, label: t.contact.phone, value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
    { icon: Mail, label: t.contact.email, value: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { icon: Clock, label: t.contact.hours, value: contactInfo.workingHours }
  ];

  return (
    <section id="contact" ref={sectionRef} className="py-20 lg:py-32 bg-white dark:bg-black transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.contact.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.contact.title} <span className="text-red-600 dark:text-red-500">{t.contact.highlight}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            {contactItems.map((item, index) => (
              <div key={index} className="contact-item">
                {item.href ? (
                  <a
                    href={item.href}
                    className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-red-50 dark:hover:bg-gray-700 transition-colors duration-300 group ring-1 ring-gray-100 dark:ring-gray-700"
                  >
                    <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 group-hover:bg-red-500 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                      <item.icon className="w-6 h-6 text-red-600 dark:text-red-400 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{item.label}</p>
                      <p className="text-lg font-medium text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {item.value}
                      </p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl ring-1 ring-gray-100 dark:ring-gray-700">
                    <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{item.label}</p>
                      <p className="text-lg font-medium text-gray-900 dark:text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {item.value}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="contact-item">
            <div className="bg-gray-100 dark:bg-gray-900 rounded-2xl overflow-hidden h-full min-h-[400px] shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d121058.92836663!2d73.7800!3d18.5200!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bf2e67461101%3A0x828d43bf9d9ee343!2sPune%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="PizzaToons Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
